import { parseChallengeUrl } from "./helpers/Challenge";
import { delta as uiDelta } from "./reducers/UI";
import { logEvent } from "./Globals";
import ScenarioChoiceDialog from "./components/base/ScenarioChoiceDialog";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { User } from "firebase/auth";
import CompositorContainer from "./components/CompositorContainer";
import UnitsProvider from "./components/base/UnitsContext";
import { navigate, navigateBack } from "./reducers/Card";
import { pageHidden, pageVisible } from "./reducers/GameActions";
import { pauseAudio, resumeAudio } from "./reducers/Settings";
import { layoutChanged, snackbarOpen } from "./reducers/UI";
import { firebaseAppAuth, getDevicePlatform, getHistoryApi } from "./Globals";
import { delta, loadProfile, reset } from "./reducers/User";

import {
  customEventFromSearch,
  isDataCenterSetupSearch,
  scenarioDetailsUrl,
  scenarioFromSearch,
  scenarioListUrl,
} from "./ScenarioUrl";
import { saveRepository, startSaveSessions } from "./SaveSession";
import { startCloudSaves } from "./CloudSaves";
import { startEventToasts } from "./EventToasts";
import { NAV_CARDS } from "./Constants";
import { useOnline } from "./components/base/OnlineStatus";
import CloudSavePrompt from "./components/base/CloudSavePrompt";
import SharedGameDialog from "./components/base/SharedGameDialog";
import { store, useAppSelector } from "./Store";
import {
  createAppTheme,
  prefersDarkMode,
  resolveThemeMode,
  setThemeMode,
} from "./Theme";
import { ThemeChoiceType } from "./Types";
import { InstallPromptProvider } from "./components/base/InstallAppButton";
import { getDesktop, isDesktopApp } from "./Desktop";

const AchievementNotifications = lazy(
  () =>
    import(
      /* webpackChunkName: "achievements-ui" */ "./achievements/Interface"
    ),
);
const MultiplayerRoot =
  process.env.REACT_APP_DESKTOP === "true"
    ? lazy(() => import("./multiplayer/Root"))
    : undefined;

// Cordova's lifecycle events, only ever fired in an app build. Returns its own teardown so the
// listeners go away with the rest of them rather than outliving the component that added them.
function setupDevice(): () => void {
  const platform = getDevicePlatform();
  // Platform-specific styles
  document.body.className += " " + platform;

  const onBackButton = () => store.dispatch(navigateBack());
  const onPause = () => {
    store.dispatch(pauseAudio());
    // In a WebView visibilitychange is not reliable, so the device events pause the clock too
    store.dispatch(pageHidden());
  };
  const onResume = () => {
    store.dispatch(resumeAudio());
    store.dispatch(pageVisible());
  };

  document.addEventListener("backbutton", onBackButton, false);
  document.addEventListener("pause", onPause, false);
  document.addEventListener("resume", onResume, false);

  return () => {
    document.removeEventListener("backbutton", onBackButton, false);
    document.removeEventListener("pause", onPause, false);
    document.removeEventListener("resume", onResume, false);
  };
}

function setupStorage(document: Document) {
  if (process.env.REACT_APP_DESKTOP === "true" && isDesktopApp()) return;
  // Alert user if cookies disabled
  // Based on https://github.com/Modernizr/Modernizr/blob/master/feature-detects/cookies.js
  try {
    document.cookie = "cookietest=1";
    const ret = document.cookie.indexOf("cookietest=") !== -1;
    document.cookie = "cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT";
    if (!ret) {
      throw new Error("Cookies disabled");
    }
  } catch (_err) {
    setTimeout(() => {
      store.dispatch(
        snackbarOpen("Please enable cookies for the app to function properly."),
      );
    }, 0);
  }
}

/**
 * Paints the app in the palette the player asked for.
 *
 * Three things have to agree on it and none of them can be reached the same way: MUI's own
 * components read a theme through context, everything styled by hand reads custom properties off
 * <html>, and the charts paint to a canvas and so have to be told in JavaScript (see Theme.tsx).
 * This is the one place that knows, so it sets all three.
 *
 * Inside the Provider because it reads the setting from the store, and above everything else
 * because a palette change has to reach the whole tree.
 */
function ThemedApp(props: { children: React.JSX.Element }): React.JSX.Element {
  const choice: ThemeChoiceType = useAppSelector(
    (state) => state.settings.theme,
  );
  // "System" is a standing instruction rather than a value, and the system can change its mind
  // while the game is open - at sunset, on a schedule, or because the player just flipped it
  const [systemDark, setSystemDark] = useState(prefersDarkMode);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemDark(query.matches);
    query.addEventListener("change", onChange);
    // An installed app can be suspended while the OS theme changes.
    const onVisible = () => {
      if (document.visibilityState === "visible") onChange();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onChange);
    // The listener can only have missed something between the first render and here
    onChange();
    return () => {
      query.removeEventListener("change", onChange);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onChange);
    };
  }, []);

  const mode =
    choice === "system"
      ? systemDark
        ? "dark"
        : "light"
      : resolveThemeMode(choice);
  // In a layout effect rather than in the render body: telling Theme.tsx wakes every chart
  // that subscribed to it, and waking a component while another one is rendering is exactly
  // what React warns about. Before paint, so neither of the two lands a frame late
  useLayoutEffect(() => {
    const syncBrowserTheme = () => {
      document.documentElement.dataset.theme = mode;
      document.documentElement.style.colorScheme = mode;
      document
        .querySelector('meta[name="color-scheme"]')
        ?.setAttribute("content", mode);
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute("content", mode === "dark" ? "#121212" : "#ffffff");
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") syncBrowserTheme();
    };
    syncBrowserTheme();
    setThemeMode(mode);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", syncBrowserTheme);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", syncBrowserTheme);
    };
  }, [mode]);

  return (
    <ThemeProvider theme={createAppTheme(mode)}>{props.children}</ThemeProvider>
  );
}

/**
 * Sits in the page flow above everything else, so it pushes the app down rather than covering
 * its navigation. A game screen has no room to give up, so there the menu button carries a dot
 * and the menu itself says why (see GameAppBar).
 */
function OfflineNotice(): React.JSX.Element | null {
  const online = useOnline();
  const onGameScreen = useAppSelector(
    (state) =>
      state.game.inGame &&
      (NAV_CARDS.includes(state.card.name) ||
        state.card.name.startsWith("BUILD_")),
  );
  return online || onGameScreen ? null : (
    <div className="offlineBanner" role="status">
      {process.env.REACT_APP_DESKTOP === "true" && isDesktopApp()
        ? "Offline — games save on this device. Steam features reconnect when Steam is online."
        : "Offline — saves use this device’s browser storage. Online features will reconnect automatically."}
    </div>
  );
}

export default function App() {
  const scenarioRouteInitialized = useRef(false);
  /**
   * All of this used to run in the component body. A function component's body runs on every
   * render, and index.tsx mounts App inside React.StrictMode, which deliberately invokes it twice
   * on mount in development -- so the visibility listener was registered twice and every hide or
   * show dispatched pause/resume twice, the auth subscription was opened twice, and the cookie
   * check ran twice. In an effect with a teardown, StrictMode's mount/unmount/mount cycle nets out
   * to exactly one of each, and nothing is left behind if App ever unmounts.
   */
  useEffect(() => {
    setupStorage(document);

    // A direct challenge link needs a real catalog entry behind it, so both the visible Back
    // button and the browser Back button stay in the app instead of returning to the referring
    // site. The ref makes this safe under StrictMode's development effect replay.
    if (!scenarioRouteInitialized.current) {
      scenarioRouteInitialized.current = true;
      const challenge = parseChallengeUrl(window.location.href);
      if (challenge) {
        const href = window.location.href;
        getHistoryApi().replaceState(null, "", scenarioListUrl());
        store.dispatch(
          navigate({ name: "NEW_GAME", skipBrowserHistory: true }),
        );
        store.dispatch(uiDelta({ challengeHref: href }));
        store.dispatch(navigate({ name: "CHALLENGE", url: href }));
        logEvent("challenge_view", { compatible: !!challenge.invitation });
      }
      const dataCenterSetup =
        !challenge && isDataCenterSetupSearch(window.location.search);
      // A data-center visitor already starts on its setup (see startupCard in reducers/Card), and
      // the guide stays the browser's Back destination because no catalog entry is pushed.
      const customEvent =
        !challenge &&
        !dataCenterSetup &&
        customEventFromSearch(window.location.search);
      if (typeof customEvent === "number") {
        const url = window.location.pathname + window.location.search;
        getHistoryApi().replaceState(null, "", scenarioListUrl());
        store.dispatch(
          navigate({ name: "NEW_GAME", skipBrowserHistory: true }),
        );
        store.dispatch(navigate({ name: "CUSTOM_GAME", url }));
      }
      const sharedScenario =
        !challenge &&
        !dataCenterSetup &&
        customEvent === undefined &&
        scenarioFromSearch(window.location.search);
      if (sharedScenario) {
        const detailsUrl = scenarioDetailsUrl(sharedScenario.id);
        getHistoryApi().replaceState(null, "", scenarioListUrl());
        store.dispatch(
          navigate({ name: "NEW_GAME", skipBrowserHistory: true }),
        );
        store.dispatch(uiDelta({ scenarioPreview: sharedScenario.id }));
        store.dispatch(navigate({ name: "NEW_GAME_DETAILS", url: detailsUrl }));
      }
    }

    const onPopState = (e: PopStateEvent) => {
      const challenge = parseChallengeUrl(window.location.href);
      if (challenge) {
        store.dispatch(uiDelta({ challengeHref: window.location.href }));
        store.dispatch(
          navigate({ name: "CHALLENGE", skipBrowserHistory: true }),
        );
        e.preventDefault();
        return;
      }
      if (isDataCenterSetupSearch(window.location.search)) {
        store.dispatch(
          navigate({ name: "DATA_CENTER_SETUP", skipBrowserHistory: true }),
        );
        e.preventDefault();
        return;
      }
      if (customEventFromSearch(window.location.search) !== undefined) {
        store.dispatch(
          navigate({ name: "CUSTOM_GAME", skipBrowserHistory: true }),
        );
        e.preventDefault();
        return;
      }
      const sharedScenario = scenarioFromSearch(window.location.search);
      if (sharedScenario) {
        store.dispatch(uiDelta({ scenarioPreview: sharedScenario.id }));
        store.dispatch(
          navigate({
            name: "NEW_GAME_DETAILS",
            skipBrowserHistory: true,
          }),
        );
      } else {
        store.dispatch(navigateBack());
      }
      e.preventDefault();
    };
    window.addEventListener("popstate", onPopState);

    let desktopFocused = true;
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden" || !desktopFocused) {
        store.dispatch(pauseAudio());
        store.dispatch(pageHidden());
      } else if (document.visibilityState === "visible") {
        store.dispatch(resumeAudio());
        store.dispatch(pageVisible());
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange, false);
    // Some mobile browsers never fire visibilitychange before the page goes away
    const onPageHide = () => {
      store.dispatch(pageHidden());
    };
    window.addEventListener("pagehide", onPageHide, false);
    // A bfcache restore can pair pageshow with pagehide without visibilitychange.
    window.addEventListener("pageshow", onVisibilityChange, false);
    const onResize = () => store.dispatch(layoutChanged());
    window.addEventListener("resize", onResize);

    const stopSaveSessions = startSaveSessions(store);
    const stopCloudSaves = startCloudSaves(store);
    const stopEventToasts = startEventToasts(store);
    let stopAchievements: (() => void) | undefined;
    let achievementsLive = true;
    void import(
      /* webpackChunkName: "achievements" */ "./achievements/Runtime"
    ).then(({ startAchievementServices }) => {
      if (achievementsLive) stopAchievements = startAchievementServices(store);
    });

    const desktop =
      process.env.REACT_APP_DESKTOP === "true" ? getDesktop() : undefined;
    let authLive = true;
    let steamProfileUid: string | undefined;
    let unsubscribeSteam: (() => void) | undefined;
    let unsubscribeDesktopFocus: (() => void) | undefined;
    let stopDesktopServices: (() => void) | undefined;
    if (process.env.REACT_APP_DESKTOP === "true" && desktop) {
      const updateSteam = (
        state: Awaited<ReturnType<NonNullable<typeof desktop>["getState"]>>,
      ) => {
        if (!authLive) return;
        if (state.steam.steamId) {
          const uid = `steam:${state.steam.steamId}`;
          store.dispatch(
            delta({
              uid,
              displayName: state.steam.displayName || "Steam player",
            }),
          );
          if (steamProfileUid !== uid) {
            steamProfileUid = uid;
            void (async () => {
              const bests: NonNullable<import("./Types").UserType["bests"]> =
                {};
              for (const entry of await saveRepository.list()) {
                try {
                  const { result } = await saveRepository.read(entry.id);
                  if (
                    result &&
                    result.score >
                      (bests[String(result.scenarioId)]?.score ?? -Infinity)
                  ) {
                    bests[String(result.scenarioId)] = {
                      score: result.score,
                      difficulty: result.difficulty,
                      date: Date.parse(entry.savedAt),
                    };
                  }
                } catch {
                  /* A damaged historical save cannot prevent playing. */
                }
              }
              if (authLive && store.getState().user.uid === uid)
                store.dispatch(delta({ bests }));
            })().catch(() => undefined);
          }
        } else {
          steamProfileUid = undefined;
          store.dispatch(reset());
        }
      };
      unsubscribeSteam = desktop.onStateChanged(updateSteam);
      unsubscribeDesktopFocus = desktop.onGameFocusChanged((focused) => {
        desktopFocused = focused;
        onVisibilityChange();
      });
      void import(/* webpackChunkName: "electron" */ "./desktop/Runtime").then(
        ({ startDesktopServices }) => {
          if (authLive) stopDesktopServices = startDesktopServices(store);
        },
        () => undefined,
      );
      void desktop
        .getState()
        .then(updateSteam)
        .catch(() => undefined);
    }
    const unsubscribeAuth = firebaseAppAuth?.onAuthStateChanged(
      (user: User | null) => {
        if (!user) {
          // Signed out, or never signed in: drop the whole slice rather than only the uid, so one
          // player's name and bests can't linger into the next player's session
          store.dispatch(reset());
          return;
        }
        // The provider's name is only a seed for the name dialog. The leaderboard name is the one
        // claimed against users/{uid}, which is what loadProfile goes and reads
        store.dispatch(
          delta({
            uid: user.uid,
            googleDisplayName: user.displayName || undefined,
          }),
        );
        store.dispatch(
          loadProfile({ uid: user.uid, googleDisplayName: user.displayName }),
        );
      },
    );

    // Only triggers on app builds. Cordova fires deviceready once, so the listeners it installs
    // have to be torn down through the teardown it hands back rather than by removing this one.
    let teardownDevice: (() => void) | undefined;
    const onDeviceReady = () => {
      teardownDevice = setupDevice();
    };
    document.addEventListener("deviceready", onDeviceReady, false);

    return () => {
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener(
        "visibilitychange",
        onVisibilityChange,
        false,
      );
      window.removeEventListener("pagehide", onPageHide, false);
      window.removeEventListener("pageshow", onVisibilityChange, false);
      window.removeEventListener("resize", onResize);
      stopSaveSessions();
      stopCloudSaves();
      stopEventToasts();
      achievementsLive = false;
      stopAchievements?.();
      if (process.env.REACT_APP_DESKTOP === "true") {
        authLive = false;
        unsubscribeSteam?.();
        unsubscribeDesktopFocus?.();
        stopDesktopServices?.();
      }
      unsubscribeAuth?.();
      document.removeEventListener("deviceready", onDeviceReady, false);
      teardownDevice?.();
    };
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemedApp>
        {/* Above the compositor, whose shouldComponentUpdate would otherwise swallow a
              settings change that did not also change the card */}
        <InstallPromptProvider>
          <div className="appFrame">
            <OfflineNotice />
            <UnitsProvider>
              <div className="appFrameBody">
                <CompositorContainer store={store} />
              </div>
              <ScenarioChoiceDialog />
              <CloudSavePrompt />
              <SharedGameDialog />
              {MultiplayerRoot && isDesktopApp() && (
                <Suspense fallback={null}>
                  <MultiplayerRoot />
                </Suspense>
              )}
              <Suspense fallback={null}>
                <AchievementNotifications />
              </Suspense>
            </UnitsProvider>
          </div>
        </InstallPromptProvider>
      </ThemedApp>
    </StyledEngineProvider>
  );
}
